// Phase 3 → 3.5 — Tiered cost-band matrix editor.
// Multiplier is the source of truth. Markup % and GP % are DERIVED, read-only.
// System hard cap on multiplier is SYSTEM_MAX_MULTIPLIER (10).
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, Save } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  useShopPricingMatrixRows,
  useUpsertMatrixRow,
  useDeleteMatrixRow,
  type ShopPricingMatrixRowRow,
} from '@/hooks/pricing/useShopPricingConfig';
import {
  SYSTEM_MAX_MULTIPLIER,
  deriveMarkupPctFromMultiplier,
  deriveGpPctFromMultiplier,
} from '@/lib/parts-financial/calculator';

interface Props { shopId: string; }

interface Draft {
  id?: string;
  category: string | null;
  min_cost: number;
  max_cost: number | null;
  multiplier: number;
  sort_order: number;
}

const fmtPct = (n: number) => `${n.toFixed(1)}%`;

function validateMultiplier(m: number): string | null {
  if (!Number.isFinite(m) || m <= 0) return 'Multiplier must be > 0';
  if (m > SYSTEM_MAX_MULTIPLIER) return `Multiplier cannot exceed ${SYSTEM_MAX_MULTIPLIER}×`;
  return null;
}

function validateRange(min: number, max: number | null): string | null {
  if (!Number.isFinite(min) || min < 0) return 'Min cost must be ≥ 0';
  if (max !== null && (!Number.isFinite(max) || max <= min)) return 'Max cost must be > min cost';
  return null;
}

export function MatrixEditor({ shopId }: Props) {
  const rowsQ = useShopPricingMatrixRows(shopId);
  const upsert = useUpsertMatrixRow();
  const del = useDeleteMatrixRow();

  const [draft, setDraft] = useState<Draft | null>(null);

  const startNew = () => setDraft({
    category: null, min_cost: 0, max_cost: null, multiplier: 1.5,
    sort_order: (rowsQ.data?.length ?? 0),
  });

  const saveDraft = async () => {
    if (!draft) return;
    const mErr = validateMultiplier(draft.multiplier);
    if (mErr) { toast({ title: mErr, variant: 'destructive' }); return; }
    const rErr = validateRange(draft.min_cost, draft.max_cost);
    if (rErr) { toast({ title: rErr, variant: 'destructive' }); return; }
    try {
      await upsert.mutateAsync({
        shop_id: shopId,
        category: draft.category,
        min_cost: draft.min_cost,
        max_cost: draft.max_cost,
        multiplier: draft.multiplier,
        sort_order: draft.sort_order,
      });
      toast({ title: 'Matrix row saved' });
      setDraft(null);
    } catch (e: any) {
      toast({ title: 'Save failed', description: e?.message, variant: 'destructive' });
    }
  };

  const updateRow = async (r: ShopPricingMatrixRowRow, patch: Partial<ShopPricingMatrixRowRow>) => {
    const next = { ...r, ...patch };
    const mErr = validateMultiplier(Number(next.multiplier));
    if (mErr) { toast({ title: mErr, variant: 'destructive' }); return; }
    const rErr = validateRange(Number(next.min_cost), next.max_cost == null ? null : Number(next.max_cost));
    if (rErr) { toast({ title: rErr, variant: 'destructive' }); return; }
    try {
      await upsert.mutateAsync(next);
    } catch (e: any) {
      toast({ title: 'Update failed', description: e?.message, variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Pricing Matrix</CardTitle>
            <CardDescription>
              Cost-band → multiplier rows. Multiplier is the source of truth — markup % and GP % are derived.
              Leave category blank for the default matrix. Leave max cost blank for an open-ended top tier.
              System hard cap: {SYSTEM_MAX_MULTIPLIER}× multiplier.
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={startNew}>
            <Plus className="h-4 w-4 mr-1" /> Add row
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-2">
          <div className="col-span-2">Category</div>
          <div className="col-span-1 text-right">Min</div>
          <div className="col-span-1 text-right">Max</div>
          <div className="col-span-2 text-right">Multiplier</div>
          <div className="col-span-2 text-right">Markup %</div>
          <div className="col-span-1 text-right">GP %</div>
          <div className="col-span-1 text-right">Order</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {(rowsQ.data ?? []).map((r) => (
          <ExistingMatrixRow
            key={r.id}
            row={r}
            onUpdate={(patch) => updateRow(r, patch)}
            onDelete={async () => {
              try { await del.mutateAsync({ id: r.id, shopId }); } catch (e: any) {
                toast({ title: 'Remove failed', description: e?.message, variant: 'destructive' });
              }
            }}
          />
        ))}

        {draft && (
          <div className="grid grid-cols-12 gap-2 items-center bg-muted/30 rounded-md p-2">
            <div className="col-span-2">
              <Input
                placeholder="(default)"
                value={draft.category ?? ''}
                onChange={(e) => setDraft({ ...draft, category: e.target.value || null })}
              />
            </div>
            <div className="col-span-1">
              <Input type="number" step="0.01" className="text-right"
                value={draft.min_cost}
                onChange={(e) => setDraft({ ...draft, min_cost: Number(e.target.value) })}
              />
            </div>
            <div className="col-span-1">
              <Input type="number" step="0.01" className="text-right" placeholder="∞"
                value={draft.max_cost ?? ''}
                onChange={(e) => setDraft({ ...draft, max_cost: e.target.value === '' ? null : Number(e.target.value) })}
              />
            </div>
            <div className="col-span-2">
              <Input type="number" step="0.01" min="0" max={SYSTEM_MAX_MULTIPLIER}
                className="text-right"
                value={draft.multiplier}
                onChange={(e) => setDraft({ ...draft, multiplier: Number(e.target.value) })}
              />
            </div>
            <div className="col-span-2 text-right text-sm tabular-nums text-muted-foreground">
              {fmtPct(deriveMarkupPctFromMultiplier(draft.multiplier))}
            </div>
            <div className="col-span-1 text-right text-sm tabular-nums text-muted-foreground">
              {fmtPct(deriveGpPctFromMultiplier(draft.multiplier))}
            </div>
            <div className="col-span-1">
              <Input type="number" step="1" className="text-right"
                value={draft.sort_order}
                onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) })}
              />
            </div>
            <div className="col-span-2 flex justify-end gap-1">
              <Button size="sm" onClick={saveDraft}>
                <Save className="h-3.5 w-3.5 mr-1" /> Add
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
            </div>
          </div>
        )}

        {!draft && !(rowsQ.data ?? []).length && (
          <div className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded-md">
            No matrix rows yet — fallback matrix is used for suggestions.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ExistingMatrixRow({
  row, onUpdate, onDelete,
}: {
  row: ShopPricingMatrixRowRow;
  onUpdate: (patch: Partial<ShopPricingMatrixRowRow>) => void;
  onDelete: () => void;
}) {
  const [local, setLocal] = useState({
    category: row.category ?? '',
    min_cost: Number(row.min_cost),
    max_cost: row.max_cost == null ? '' : String(Number(row.max_cost)),
    multiplier: Number(row.multiplier),
    sort_order: row.sort_order,
  });

  const dirty =
    (local.category || null) !== row.category ||
    local.min_cost !== Number(row.min_cost) ||
    (local.max_cost === '' ? null : Number(local.max_cost)) !== (row.max_cost == null ? null : Number(row.max_cost)) ||
    local.multiplier !== Number(row.multiplier) ||
    local.sort_order !== row.sort_order;

  const overCap = local.multiplier > SYSTEM_MAX_MULTIPLIER;

  return (
    <div className="grid grid-cols-12 gap-2 items-center bg-card rounded-md border p-2">
      <div className="col-span-2">
        <Input value={local.category}
          placeholder="(default)"
          onChange={(e) => setLocal({ ...local, category: e.target.value })} />
      </div>
      <div className="col-span-1">
        <Input type="number" step="0.01" className="text-right"
          value={local.min_cost}
          onChange={(e) => setLocal({ ...local, min_cost: Number(e.target.value) })} />
      </div>
      <div className="col-span-1">
        <Input type="number" step="0.01" className="text-right" placeholder="∞"
          value={local.max_cost}
          onChange={(e) => setLocal({ ...local, max_cost: e.target.value })} />
      </div>
      <div className="col-span-2">
        <Input type="number" step="0.01" min="0" max={SYSTEM_MAX_MULTIPLIER}
          className={`text-right ${overCap ? 'border-destructive' : ''}`}
          value={local.multiplier}
          onChange={(e) => setLocal({ ...local, multiplier: Number(e.target.value) })} />
      </div>
      <div className="col-span-2 text-right text-sm tabular-nums text-muted-foreground">
        {fmtPct(deriveMarkupPctFromMultiplier(local.multiplier))}
      </div>
      <div className="col-span-1 text-right text-sm tabular-nums text-muted-foreground">
        {fmtPct(deriveGpPctFromMultiplier(local.multiplier))}
      </div>
      <div className="col-span-1">
        <Input type="number" step="1" className="text-right"
          value={local.sort_order}
          onChange={(e) => setLocal({ ...local, sort_order: Number(e.target.value) })} />
      </div>
      <div className="col-span-2 flex justify-end gap-1">
        <Button size="sm" disabled={!dirty || overCap}
          onClick={() => onUpdate({
            category: local.category || null,
            min_cost: local.min_cost,
            max_cost: local.max_cost === '' ? null : Number(local.max_cost),
            multiplier: local.multiplier,
            sort_order: local.sort_order,
          })}>
          <Save className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
